import { AxiosError } from 'axios'
import { ErrorCode } from '@ancon/wildcat-types'

import {
  CheckoutInvalidDataErrorAdditionalData,
  CheckoutOutOfStockAdditionalData,
  CheckoutUnavailableOrderTimeErrorAdditionalData,
  UnavailableOverrideMenuItems,
} from '../checkout/types'
import { ProductPriceChangeAdditionalData } from '../../api/endpoints/core/checkouts'

/**
 * Note: Some of white-labeled apps belong to same tenant.
 * 1. Nordrest Tenant
 *    - Lunchbox
 *    - Nordrest Axis
 * 2. Göteborgs Familjen Tenant
 *    - Havsbaren
 *    - Moreno Pizza
 *    - Tacos & Tequila
 *    - Butcher's Market
 *    - Kafé Magasinet
 * 3. Kläppen Restauranger Tenant
 *    - Klappen Bageriet
 *    - Klappen Halster
 *    - Klappen Toppstugan
 */
export enum WhiteLabeledAppName {
  HolyGreens = 'holy greens',
  UrbanDeli = 'urban deli',
  Corner = 'corner',
  Lunchbox = 'lunchbox',
  NordrestAxis = 'nordrest axis',
  HeatRestauranger = 'heat restauranger',
  Olandschoklad = 'olandschoklad',
  Havsbaren = 'havsbaren',
  MorenoPizza = 'moreno pizza',
  TacosAndTequila = 'tacos and tequila',
  ButchersMarket = 'butchers market',
  KafeMagasinet = 'kafe magasinet',
  Svennepizzan = 'svennepizzan',
  Keemchi = 'keemchi',
  GronaLund = 'grona lund',
  BruketIWiared = 'bruket i wiared',
  LundsKommun = 'lunds kommun',
  KlappenBageriet = 'klappen bageriet',
  KlappenHalster = 'klappen halster',
  KlappenToppstugan = 'klappen toppstugan',
  Brutale = 'brutale',
  Strandgatan = 'strandgatan',
}

export type CodedError = {
  code: ErrorCode
  message?: string
  additionalData: Record<string, unknown>
}

export interface ErrorResponseAdditionalData extends Record<
  ErrorCode,
  unknown
> {
  [ErrorCode.PaymentAcceptOutOfStock]: CheckoutOutOfStockAdditionalData[]
  [ErrorCode.CheckoutPriceChanged]: ProductPriceChangeAdditionalData[]
  [ErrorCode.RequestDataNotValid]: CheckoutInvalidDataErrorAdditionalData[]
  [ErrorCode.CheckoutUnavailableOrderTime]: CheckoutUnavailableOrderTimeErrorAdditionalData
  [ErrorCode.CheckoutItemUnavailableDueToOverrideMenu]: UnavailableOverrideMenuItems
}

export type APIErrorResponse<T extends ErrorCode = never> = {
  errorCode?: ErrorCode
  message?: string
  additionalData?: ErrorResponseAdditionalData[T]
}

export type AppGenericErrorType =
  | AxiosError<{ error: { id: string } }>
  | AxiosError<APIErrorResponse>
  | CodedError
  | unknown

export enum ErrorModalType {
  CheckoutItemPriceChanged,
  CreateCheckoutProductOutOfStock,
  UpdateCheckoutProductOutOfStock,
  UpdateCheckoutItemQuantityOutOfStock,
  GeneralCheckoutError,
  ProductOutOfStock,
  OutletNotAvailableForOrders,
  CheckoutPaymentError,
  CheckoutPaymentPriceChanged,
  CheckoutFetchError,
  CheckoutAddTipError,
  CheckoutBoxPickupUnAvailableProducts,
  CheckPaymentError,
  CheckoutUnavailableOrderTime,
  CheckoutUnavailableProductsDueToOverrideMenu,
  SurfboardValidationError,
  PayAndGoSessionExpired,
  CheckoutItemsNotValid,
}

export type AppError = {
  name: string
  message: string
}

export type ErrorInfo = {
  title: string
  message: string
}

export type InformativeError<T extends ErrorCode = never> = ErrorInfo & {
  errorModalType?: ErrorModalType
  additionalData?: ErrorResponseAdditionalData[T]
}

export type AppReducerState = {
  isErrorModalVisible: boolean
  errorModalConfig: InformativeError | null

  isCartDrawerVisible: boolean
  /** The cart rail on large desktops was collapsed by the user */
  isCartRailCollapsed: boolean
  isMobileDrawerVisible: boolean
  isSwitchLanguageModalVisible: boolean
  orderDrawerOrderId: string | null

  isSignalROnline: boolean
}

export enum PurchaseTermsSectionType {
  PersonalInformation = 'personalInfo',
  Ordering = 'ordering',
  Products = 'products',
  Payment = 'payment',
  Delivery = 'delivery',
  Returns = 'returns',
  Abuse = 'abuse',
  Amendments = 'amendments',
}

export type MapMouseEventHandler = (event: google.maps.MapMouseEvent) => void

/** Marker configuration for `MapView` */
export interface MapViewMarkerConfig {
  draggable?: boolean
  icon?: string | google.maps.Icon | google.maps.Symbol
  optimized?: boolean
  onDragend?: MapMouseEventHandler
}

/** The order flows the step bar knows about */
export enum OrderFlow {
  /** Guest or private customer */
  Standard = 'standard',
  /** Company profile, individual order */
  Company = 'company',
  /** Company group order, seen by the host */
  Group = 'group',
  /** Someone who joined a group order */
  Attendee = 'attendee',
}

export enum OrderFlowStepId {
  Start = 'start',
  Products = 'products',
  TeamOrders = 'teamOrders',
  Review = 'review',
  Fulfilment = 'fulfilment',
  Join = 'join',
  Confirm = 'confirm',
}

export type StepStatus = 'done' | 'current' | 'upcoming' | 'error'

export type OrderFlowStep = {
  id: OrderFlowStepId
  /** Translation key in the `common` namespace */
  labelKey: string
  status: StepStatus
  /** Whether this is the step the user is on, also when its status is error */
  isCurrent: boolean
}

export enum StatusTrackerStageId {
  Received = 'received',
  Confirmed = 'confirmed',
  Preparing = 'preparing',
  Ready = 'ready',
  OnItsWay = 'onItsWay',
}
